import React, { useState, useEffect, useCallback } from 'react';
import { Search, Download, Loader2, ChevronLeft, ChevronRight, ExternalLink, Phone, Star, X, RefreshCw, AlertTriangle, CheckCircle, ShieldQuestion, Trash2, RotateCcw, UserPlus } from 'lucide-react';
import canvassingService from '../../services/canvassingService';
import { tokenManager } from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';
import useScrapeStream from '../../hooks/useScrapeStream';
import HygieneRatingBadge from './HygieneRatingBadge';

const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [towns, setTowns] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [error, setError] = useState(null);
  const [revalidating, setRevalidating] = useState(false);
  const { confirmDialog, confirm } = useConfirm();
  const { validationProgress, isCompleted: streamCompleted, connectToScrapeStream, resetScrapeStream } = useScrapeStream();

  const [memberCounts, setMemberCounts] = useState({});
  const [categoryCounts, setCategoryCounts] = useState({});

  // Lead assignment state
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignUserId, setAssignUserId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Filters — default to 'approved' status
  const [filters, setFilters] = useState({
    search: '',
    categoryId: '',
    postTown: '',
    memberId: '',
    isChain: '',
    aiStatus: 'approved',
    assignedUserId: '',
    sortBy: 'business_name',
    sortDir: 'asc'
  });
  const [searchInput, setSearchInput] = useState('');
  const searchTimerRef = React.useRef(null);

  const loadLeads = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.limit,
        ...filters
      };
      // Remove empty params
      Object.keys(params).forEach(k => { if (params[k] === '' || params[k] === undefined) delete params[k]; });

      const data = await canvassingService.getLeads(params);
      setLeads(data.leads || []);
      setPagination(data.pagination || { page, limit: 50, total: 0, totalPages: 0 });
      setSelectedLeadIds(new Set());
    } catch (err) {
      setError('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  const loadMeta = useCallback(async () => {
    try {
      const [statsData, catsData, townsData, membersData, usersData] = await Promise.all([
        canvassingService.getLeadStats(),
        canvassingService.getCategories(),
        canvassingService.getLeadTowns(),
        canvassingService.getMembers(),
        canvassingService.getAssignableUsers()
      ]);
      setStats(statsData);
      setCategories(catsData.categories || []);
      setTowns(townsData.towns || []);
      setMembers(membersData.members || []);
      setAssignableUsers(usersData.users || []);
    } catch (err) {
      // Non-critical — stats/meta can fail silently
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadLeads(1);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch per-member and per-category lead counts whenever filters change
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const baseParams = { ...filters };
        Object.keys(baseParams).forEach(k => { if (baseParams[k] === '' || baseParams[k] === undefined) delete baseParams[k]; });
        delete baseParams.sortBy;
        delete baseParams.sortDir;

        const memberParams = { ...baseParams };
        delete memberParams.memberId;

        const catParams = { ...baseParams };
        delete catParams.categoryId;

        const [memberData, catData] = await Promise.all([
          canvassingService.getMemberCounts(memberParams),
          canvassingService.getCategoryCounts(catParams)
        ]);
        setMemberCounts(memberData.counts || {});
        setCategoryCounts(catData.counts || {});
      } catch {
        // Non-critical
      }
    };
    fetchCounts();
  }, [filters.categoryId, filters.postTown, filters.isChain, filters.aiStatus, filters.search, filters.memberId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: value }));
    }, 400);
  };

  const handleSort = (column) => {
    setFilters(prev => ({
      ...prev,
      sortBy: column,
      sortDir: prev.sortBy === column && prev.sortDir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleExport = () => {
    const params = { ...filters };
    Object.keys(params).forEach(k => { if (params[k] === '' || params[k] === undefined) delete params[k]; });

    // Build export URL with auth token
    let baseUrl = process.env.REACT_APP_API_URL || '';
    if (!baseUrl.includes('/api')) baseUrl += '/api';
    const token = tokenManager.getToken();
    const queryString = new URLSearchParams({ ...params, token }).toString();
    window.open(`${baseUrl}/canvassing/leads/export?${queryString}`, '_blank');
  };

  const handleDeleteLead = async (id) => {
    const ok = await confirm({
      title: 'Delete Lead',
      message: 'This lead will be permanently removed from the database.',
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      await canvassingService.deleteLead(id);
      setLeads(prev => prev.filter(l => l.id !== id));
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));
    } catch (err) {
      setError('Failed to delete lead');
    }
  };

  // Checkbox handlers
  const toggleSelectLead = (id) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeadIds.size === leads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(leads.map(l => l.id)));
    }
  };

  const handleAssignLeads = async () => {
    if (selectedLeadIds.size === 0) return;
    try {
      setAssigning(true);
      await canvassingService.assignLeads([...selectedLeadIds], assignUserId || null);
      setSelectedLeadIds(new Set());
      setAssignUserId('');
      loadLeads(pagination.page);
    } catch (err) {
      setError('Failed to assign leads');
    } finally {
      setAssigning(false);
    }
  };

  // Build a human-readable description of what's currently filtered
  const getFilterDescription = () => {
    const parts = [];
    if (filters.aiStatus === 'approved') parts.push('Approved');
    else if (filters.aiStatus === 'excluded') parts.push('Excluded');
    else if (filters.aiStatus === 'unvalidated') parts.push('Unvalidated');
    if (filters.categoryId) {
      const cat = categories.find(c => String(c.id) === String(filters.categoryId));
      if (cat) parts.push(cat.category_name);
    }
    if (filters.postTown) parts.push(filters.postTown);
    if (filters.memberId) {
      const m = memberLookup[filters.memberId];
      if (m) parts.push(m.name);
    }
    if (filters.isChain === 'true') parts.push('Chains only');
    else if (filters.isChain === 'false') parts.push('Non-chains');
    if (filters.search) parts.push(`matching "${filters.search}"`);
    return parts.length > 0 ? parts.join(', ') : 'all';
  };

  const handleRevalidate = async () => {
    const count = pagination.total || 0;
    const desc = getFilterDescription();
    const ok = await confirm({
      title: 'Revalidate Leads',
      message: `This will reset and re-validate ${count.toLocaleString()} leads (${desc}). This uses API credits and may take several minutes.`,
      variant: 'warning',
      confirmLabel: 'Revalidate'
    });
    if (!ok) return;
    try {
      setRevalidating(true);
      setError(null);
      resetScrapeStream();

      // Pass current filters so only matching leads get reset
      const activeFilters = { ...filters };
      Object.keys(activeFilters).forEach(k => { if (activeFilters[k] === '' || activeFilters[k] === undefined) delete activeFilters[k]; });
      // Remove sort params — not relevant for revalidation
      delete activeFilters.sortBy;
      delete activeFilters.sortDir;

      // Connect SSE FIRST so we don't miss early progress events
      await connectToScrapeStream('revalidate');

      await canvassingService.revalidateFiltered({ filters: activeFilters });
    } catch (err) {
      setError('Failed to start revalidation');
      setRevalidating(false);
    }
  };

  // Auto-refresh when revalidation completes via SSE
  useEffect(() => {
    if (streamCompleted && revalidating) {
      setRevalidating(false);
      resetScrapeStream();
      loadLeads(pagination.page);
      loadMeta();
    }
  }, [streamCompleted, revalidating]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setFilters({ search: '', categoryId: '', postTown: '', memberId: '', isChain: '', aiStatus: 'approved', assignedUserId: '', sortBy: 'business_name', sortDir: 'asc' });
    setSearchInput('');
  };

  // Stat card click handlers
  const handleStatClick = (filterKey, filterValue) => {
    if (filters[filterKey] === filterValue) {
      // Toggle off if already active
      setFilters(prev => ({ ...prev, [filterKey]: '' }));
    } else {
      setFilters(prev => ({ ...prev, [filterKey]: filterValue }));
    }
  };

  const hasActiveFilters = filters.search || filters.categoryId || filters.postTown || filters.memberId || filters.isChain || filters.assignedUserId || (filters.aiStatus && filters.aiStatus !== 'approved');

  // Build a flat lookup of member_id → { name, postcode_areas } from members hierarchy
  const memberLookup = React.useMemo(() => {
    const lookup = {};
    for (const f of members) {
      // Strip the code suffix like "(TW1)" from member_name for cleaner display
      const cleanName = f.member_name.replace(/\s*\([^)]+\)\s*$/, '');
      lookup[f.member_id] = { name: cleanName, postcode_areas: f.postcode_areas };
      for (const emp of f.employees) {
        const empClean = emp.member_name.replace(/\s*\([^)]+\)\s*$/, '');
        lookup[emp.member_id] = { name: empClean, postcode_areas: emp.postcode_areas };
      }
    }
    return lookup;
  }, [members]);

  // Build flat user lookup for assigned_user display (includes postcode areas)
  const userLookup = React.useMemo(() => {
    const lookup = {};
    for (const f of assignableUsers) {
      lookup[f.id] = { name: f.name, postcode_areas: f.postcode_areas || '' };
      for (const emp of (f.employees || [])) {
        lookup[emp.id] = { name: emp.name, postcode_areas: emp.postcode_areas || '' };
      }
    }
    return lookup;
  }, [assignableUsers]);

  // Helper to get validation status for a lead
  const getAiStatus = (lead) => {
    if (!lead.ai_validated) return 'unvalidated';
    if (lead.location_valid === 1 && lead.category_valid === 1) return 'approved';
    return 'excluded';
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
        </div>
      )}

      {/* Stats Bar — clickable filters */}
      {stats?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatClickCard
            label="Total Leads"
            value={(stats.stats.total_leads || 0).toLocaleString()}
            color="gray"
            active={!filters.aiStatus && !filters.isChain}
            onClick={() => setFilters(prev => ({ ...prev, aiStatus: '', isChain: '' }))}
          />
          <StatClickCard
            label="Approved"
            value={(stats.stats.ai_approved || 0).toLocaleString()}
            color="green"
            icon={<CheckCircle size={14} />}
            active={filters.aiStatus === 'approved'}
            onClick={() => handleStatClick('aiStatus', 'approved')}
          />
          <StatClickCard
            label="Excluded"
            value={(stats.stats.ai_excluded || 0).toLocaleString()}
            color="red"
            icon={<AlertTriangle size={14} />}
            active={filters.aiStatus === 'excluded'}
            onClick={() => handleStatClick('aiStatus', 'excluded')}
          />
          <StatClickCard
            label="Unvalidated"
            value={(stats.stats.unvalidated || 0).toLocaleString()}
            color="yellow"
            icon={<ShieldQuestion size={14} />}
            active={filters.aiStatus === 'unvalidated'}
            onClick={() => handleStatClick('aiStatus', 'unvalidated')}
          />
          <StatClickCard
            label="Chains"
            value={(stats.stats.chains || 0).toLocaleString()}
            color="orange"
            active={filters.isChain === 'true'}
            onClick={() => handleStatClick('isChain', 'true')}
          />
          <StatClickCard
            label="Towns"
            value={stats.stats.unique_towns || 0}
            color="blue"
          />
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-64">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search business name, address, phone, postcode..."
              className="w-full pl-9 pr-8 py-2 border rounded-lg text-sm"
            />
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            {searchInput && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Category filter */}
          <select
            value={filters.categoryId}
            onChange={(e) => setFilters(prev => ({ ...prev, categoryId: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Categories</option>
            {categories.map(c => {
              const count = categoryCounts[c.id] || 0;
              return (
                <option key={c.id} value={c.id}>{c.category_name} ({count.toLocaleString()})</option>
              );
            })}
          </select>

          {/* Town filter */}
          <select
            value={filters.postTown}
            onChange={(e) => setFilters(prev => ({ ...prev, postTown: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Towns</option>
            {towns.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          {/* Member/Technician filter */}
          <select
            value={filters.memberId}
            onChange={(e) => setFilters(prev => ({ ...prev, memberId: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Technicians</option>
            {members.map(f => {
              // Sum counts for franchisee + all employees
              const allIds = [f.member_id, ...f.employees.map(e => e.member_id)];
              const groupTotal = allIds.reduce((sum, id) => sum + (memberCounts[id] || 0), 0);
              const companyLabel = f.company_name
                ? f.company_name.replace(/PestControl\.co\.uk\s*/i, '').replace(/^\(/, '').replace(/\)\s*Ltd$/, ' Ltd')
                : f.member_name;
              return (
                <optgroup key={f.member_id} label={`${companyLabel} (${groupTotal})`}>
                  {[f, ...f.employees].map(emp => {
                    const name = emp.member_name.replace(/\s*\([^)]+\)\s*$/, '');
                    const count = memberCounts[emp.member_id] || 0;
                    return (
                      <option key={emp.member_id} value={emp.member_id}>
                        {name} ({count})
                      </option>
                    );
                  })}
                </optgroup>
              );
            })}
          </select>

          {/* Chain filter */}
          <select
            value={filters.isChain}
            onChange={(e) => setFilters(prev => ({ ...prev, isChain: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Businesses</option>
            <option value="false">Hide Chains</option>
            <option value="true">Chains Only</option>
          </select>

          {/* Email filter */}
          <select
            value={filters.hasEmail || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, hasEmail: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All (Email)</option>
            <option value="yes">Has Email</option>
            <option value="no">No Email</option>
          </select>

          {/* Status filter */}
          <select
            value={filters.aiStatus}
            onChange={(e) => setFilters(prev => ({ ...prev, aiStatus: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Status</option>
            <option value="approved">Approved</option>
            <option value="excluded">Excluded</option>
            <option value="unvalidated">Not Validated</option>
          </select>

          {/* Assigned User filter */}
          <select
            value={filters.assignedUserId}
            onChange={(e) => setFilters(prev => ({ ...prev, assignedUserId: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All (Assigned)</option>
            <option value="unassigned">Unassigned</option>
            {assignableUsers.map(f => (
              <optgroup key={f.id} label={f.name}>
                <option value={f.id}>{f.name}</option>
                {(f.employees || []).map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </optgroup>
            ))}
          </select>

          {/* Actions */}
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-gray-700 underline">
              Clear filters
            </button>
          )}

          <button
            onClick={handleExport}
            className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
          >
            <Download size={14} /><span>Export CSV</span>
          </button>

          <button
            onClick={handleRevalidate}
            disabled={revalidating}
            className="flex items-center space-x-1 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50"
            title="Reset and re-validate currently filtered leads"
          >
            {revalidating ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
            <span>Revalidate</span>
          </button>

          <button onClick={() => { loadLeads(pagination.page); loadMeta(); }} className="text-gray-400 hover:text-gray-600 p-2">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Revalidation Progress */}
      {revalidating && (
        <div className="bg-white border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Loader2 size={16} className="animate-spin text-purple-600" />
              <span className="text-sm font-medium text-gray-800">Revalidating leads...</span>
            </div>
            {validationProgress && (
              <span className="text-sm text-gray-500">
                {validationProgress.validated}/{validationProgress.total}
                {validationProgress.chainsFound > 0 && ` — ${validationProgress.chainsFound} chains found`}
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: validationProgress && validationProgress.total > 0
                  ? `${Math.round(validationProgress.validated / validationProgress.total * 100)}%`
                  : '0%'
              }}
            />
          </div>
          {validationProgress && validationProgress.total > 0 && (
            <div className="text-xs text-gray-400 mt-1">
              {Math.round(validationProgress.validated / validationProgress.total * 100)}% complete
            </div>
          )}
        </div>
      )}

      {/* Bulk Assign Action Bar */}
      {selectedLeadIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-blue-800">
              {selectedLeadIds.size} lead{selectedLeadIds.size !== 1 ? 's' : ''} selected
            </span>
            <select
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm"
            >
              <option value="">Unassign</option>
              {assignableUsers.map(f => (
                <optgroup key={f.id} label={f.name}>
                  <option value={f.id}>{f.name}</option>
                  {(f.employees || []).map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <button
              onClick={handleAssignLeads}
              disabled={assigning}
              className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {assigning ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              <span>{assignUserId ? 'Assign' : 'Unassign'}</span>
            </button>
          </div>
          <button
            onClick={() => setSelectedLeadIds(new Set())}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-pest-blue" size={24} />
            <span className="ml-2 text-gray-500">Loading leads...</span>
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg mb-2">No leads found</p>
            <p className="text-sm">Run an extraction from the "Extract Data" tab to populate leads.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={leads.length > 0 && selectedLeadIds.size === leads.length}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <SortHeader label="Business Name" column="business_name" current={filters.sortBy} dir={filters.sortDir} onSort={handleSort} />
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Address</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Postcode</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Phone</th>
                    <SortHeader label="Category" column="category_name" current={filters.sortBy} dir={filters.sortDir} onSort={handleSort} />
                    <SortHeader label="Town" column="post_town" current={filters.sortBy} dir={filters.sortDir} onSort={handleSort} />
                    <SortHeader label="Rating" column="rating" current={filters.sortBy} dir={filters.sortDir} onSort={handleSort} />
                    <SortHeader label="Hygiene" column="fhrs_rating" current={filters.sortBy} dir={filters.sortDir} onSort={handleSort} />
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Assigned To</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Website</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Email</th>
                    <th className="text-center px-3 py-2 font-medium text-gray-600">Social</th>
                    <th className="text-center px-3 py-2 font-medium text-gray-600"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.map((lead) => {
                    const aiStatus = getAiStatus(lead);
                    return (
                      <tr key={lead.id} className={`hover:bg-gray-50 ${aiStatus === 'excluded' ? 'bg-red-50/30' : ''} ${selectedLeadIds.has(lead.id) ? 'bg-blue-50/50' : ''}`}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedLeadIds.has(lead.id)}
                            onChange={() => toggleSelectLead(lead.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-800">{lead.business_name}</div>
                          {lead.google_category && (
                            <div className="text-xs text-gray-400">{lead.google_category}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-600 text-xs">
                          {lead.address_line_1 && (
                            <div>{lead.address_line_1}</div>
                          )}
                          {lead.address_line_2 && (
                            <div className="text-gray-400">{lead.address_line_2}</div>
                          )}
                          {lead.address_town && (
                            <div>{lead.address_town}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">
                          {lead.address_postcode || ''}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {lead.phone_number && (
                            <a href={`tel:${lead.phone_number}`} className="flex items-center space-x-1 text-blue-600 hover:underline text-xs">
                              <Phone size={10} /><span>{lead.phone_number}</span>
                            </a>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {lead.category_name}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">{lead.post_town}</td>
                        <td className="px-3 py-2 text-center whitespace-nowrap">
                          {lead.rating && (
                            <span className="flex items-center justify-center space-x-1 text-xs">
                              <Star size={10} className="text-yellow-500 fill-yellow-500" />
                              <span>{lead.rating}</span>
                              {lead.review_count > 0 && <span className="text-gray-400">({lead.review_count})</span>}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center whitespace-nowrap">
                          <HygieneRatingBadge lead={lead} />
                        </td>
                        <td className="px-3 py-2">
                          <StatusBadge lead={lead} status={aiStatus} />
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {lead.assigned_user_name ? (
                            <div>
                              <div className="font-medium text-blue-700">{lead.assigned_user_name}</div>
                              {lead.assigned_user_id && userLookup[lead.assigned_user_id]?.postcode_areas && (
                                <div className="text-gray-400">{userLookup[lead.assigned_user_id].postcode_areas}</div>
                              )}
                            </div>
                          ) : lead.member_id && memberLookup[lead.member_id] ? (
                            <div>
                              <div className="font-medium text-gray-700">{memberLookup[lead.member_id].name}</div>
                              {memberLookup[lead.member_id].postcode_areas && (
                                <div className="text-gray-400">{memberLookup[lead.member_id].postcode_areas}</div>
                              )}
                            </div>
                          ) : lead.member_name ? (
                            <div className="text-gray-600">{lead.member_name.replace(/\s*\([^)]+\)\s*$/, '')}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          {lead.website && (
                            <a
                              href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                              title={lead.website}
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {lead.email ? (
                            <a
                              href={`mailto:${lead.email.split(';')[0].trim()}`}
                              className="text-blue-600 hover:underline"
                              title={lead.email}
                            >
                              {lead.email.split(';')[0].trim().length > 25
                                ? lead.email.split(';')[0].trim().substring(0, 25) + '...'
                                : lead.email.split(';')[0].trim()}
                            </a>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center space-x-1">
                            {lead.facebook_url && (
                              <a href={lead.facebook_url} target="_blank" rel="noopener noreferrer" title="Facebook">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                              </a>
                            )}
                            {lead.twitter_url && (
                              <a href={lead.twitter_url} target="_blank" rel="noopener noreferrer" title="X / Twitter">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="#000000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                              </a>
                            )}
                            {lead.instagram_url && (
                              <a href={lead.instagram_url} target="_blank" rel="noopener noreferrer" title="Instagram">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="#E4405F"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z"/></svg>
                              </a>
                            )}
                            {lead.linkedin_url && (
                              <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" title="LinkedIn">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                              </a>
                            )}
                            {lead.youtube_url && (
                              <a href={lead.youtube_url} target="_blank" rel="noopener noreferrer" title="YouTube">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF0000"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                              </a>
                            )}
                            {lead.tiktok_url && (
                              <a href={lead.tiktok_url} target="_blank" rel="noopener noreferrer" title="TikTok">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="#000000"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => handleDeleteLead(lead.id)}
                            className="text-gray-400 hover:text-red-600 p-1"
                            title="Delete lead"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total.toLocaleString()} leads
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => loadLeads(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => loadLeads(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

/** Clickable stat card that acts as a filter toggle */
const StatClickCard = ({ label, value, color = 'gray', icon, active, onClick }) => {
  const colorMap = {
    gray: { bg: 'bg-white', text: 'text-gray-800', activeBg: 'bg-gray-100', border: 'border-gray-200', activeBorder: 'border-gray-400' },
    green: { bg: 'bg-white', text: 'text-green-600', activeBg: 'bg-green-50', border: 'border-gray-200', activeBorder: 'border-green-500' },
    red: { bg: 'bg-white', text: 'text-red-600', activeBg: 'bg-red-50', border: 'border-gray-200', activeBorder: 'border-red-500' },
    yellow: { bg: 'bg-white', text: 'text-yellow-600', activeBg: 'bg-yellow-50', border: 'border-gray-200', activeBorder: 'border-yellow-500' },
    orange: { bg: 'bg-white', text: 'text-orange-600', activeBg: 'bg-orange-50', border: 'border-gray-200', activeBorder: 'border-orange-500' },
    blue: { bg: 'bg-white', text: 'text-blue-600', activeBg: 'bg-blue-50', border: 'border-gray-200', activeBorder: 'border-blue-500' },
    purple: { bg: 'bg-white', text: 'text-purple-600', activeBg: 'bg-purple-50', border: 'border-gray-200', activeBorder: 'border-purple-500' }
  };
  const c = colorMap[color] || colorMap.gray;

  return (
    <button
      onClick={onClick}
      className={`rounded-lg px-4 py-3 border text-left transition-all ${
        active ? `${c.activeBg} ${c.activeBorder} ring-1 ring-offset-0 ring-${color === 'gray' ? 'gray-400' : color + '-500'}` : `${c.bg} ${c.border} hover:border-gray-300`
      } ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center space-x-1">
        {icon && <span className={c.text}>{icon}</span>}
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className={`text-xl font-bold ${c.text}`}>{value}</div>
    </button>
  );
};

/** Status badge with inline exclusion reasons */
const StatusBadge = ({ lead, status }) => {
  if (status === 'approved') {
    return (
      <div>
        {lead.is_chain ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
            {lead.chain_name || 'Chain'}
          </span>
        ) : (
          <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle size={10} /><span>Approved</span>
          </span>
        )}
      </div>
    );
  }

  if (status === 'excluded') {
    const reasons = [];
    if (lead.location_valid === 0) reasons.push('Wrong location');
    if (lead.category_valid === 0) reasons.push('Wrong category');

    return (
      <div className="text-xs">
        <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700 mb-0.5">
          <AlertTriangle size={10} /><span>Excluded</span>
        </span>
        {reasons.length > 0 && (
          <div className="text-red-600 mt-0.5">{reasons.join(', ')}</div>
        )}
        {lead.ai_notes && (
          <div className="text-gray-400 mt-0.5">{lead.ai_notes}</div>
        )}
      </div>
    );
  }

  // unvalidated
  return (
    <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      <span>Pending</span>
    </span>
  );
};

const SortHeader = ({ label, column, current, dir, onSort }) => {
  const isActive = current === column;
  return (
    <th
      className="text-left px-3 py-2 font-medium text-gray-600 cursor-pointer hover:text-gray-800 select-none whitespace-nowrap"
      onClick={() => onSort(column)}
    >
      {label}
      {isActive && (
        <span className="ml-1 text-pest-blue">{dir === 'asc' ? '\u2191' : '\u2193'}</span>
      )}
    </th>
  );
};

export default LeadsTab;
